/**
 * Keyboard shortcuts map — single source of truth across shells.
 *
 * Originally extracted from `AppShell.tsx` per WP-E4 step 4.g so the
 * shortcut help page (GVW-155) and `AppShell` consume the same
 * definitions. WP-11 (Final design-system compliance sweep) extends
 * this module so the **TwinShell** uses exactly the same map: every
 * shortcut now declares which shell(s) it applies to, and the
 * `useUnifiedKeyboardShortcuts` hook below wires the same key
 * dispatch table into either shell.
 *
 * Each entry defines:
 *   - `keys`: array of keys / modifiers shown in the help table
 *   - `description`: user-facing description
 *   - `group`: functional grouping for display organization
 *   - `shells`: which shell(s) the shortcut binds in (`'twin'` and
 *     `'workspace'`; both by default)
 *   - `conditions`: optional context where the shortcut applies
 *   - `event` (optional): canonical action emitted on the
 *     `viewer:shortcut` `CustomEvent` so shell-side handlers can
 *     subscribe without inventing their own keymap.
 *
 * Source-of-truth citations:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
 *     §10.3 ("Keyboard shortcuts documented in the help drawer and
 *     consistent between TwinShell and AppShell").
 *   - WP-2 supervisor verdict §10 ("AppShell-only keyboard map" gap
 *     deferred to WP-11).
 */

import { useEffect } from 'react';

export type KeyboardShortcutShell = 'twin' | 'workspace';

export type KeyboardShortcutAction =
  | 'pan-up'
  | 'pan-down'
  | 'pan-left'
  | 'pan-right'
  | 'zoom-in'
  | 'zoom-out'
  | 'fit-to-visible'
  | 'focus-layers'
  | 'tool-measure'
  | 'tool-area'
  | 'tool-pan'
  | 'tool-select'
  | 'three-d-look-at-selected'
  | 'three-d-camera-pan'
  | 'three-d-reset-orbit'
  | 'three-d-toggle-section'
  | 'three-d-toggle-isolate'
  | 'three-d-toggle-ghost'
  | 'pointcloud-toggle-slice'
  | 'pointcloud-decrease-point-size'
  | 'pointcloud-increase-point-size'
  | 'pointcloud-color-rgb'
  | 'pointcloud-color-elevation'
  | 'pointcloud-color-intensity'
  | 'pointcloud-color-classification'
  | 'pointcloud-color-deviation'
  | 'clear-scene-selection'
  | 'global-escape'
  | 'lor-home';

export interface KeyboardShortcut {
  keys: string[];
  description: string;
  group: 'global' | 'map' | 'three-d' | 'pointcloud' | 'timeline';
  /**
   * Which shells the shortcut applies to. Defaults are baked in below
   * and both shells receive every shortcut unless explicitly scoped
   * to one. Used by `useUnifiedKeyboardShortcuts` to gate the
   * dispatch and by the help drawer to label per-shell rows.
   */
  shells: ReadonlyArray<KeyboardShortcutShell>;
  conditions?: string;
  /** Canonical action id, when the shortcut maps to a single
   *  shell-agnostic action surfaced via the `viewer:shortcut` event. */
  event?: KeyboardShortcutAction;
}

export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  // Global / cross-mode
  {
    keys: ['Esc'],
    description: 'Clear active tool or close dialog',
    group: 'global',
    shells: ['twin', 'workspace'],
    event: 'global-escape',
  },
  // WP-12 — Esc inside the right-rail ToolCardDock clears the
  // scene selection and returns focus to the scene tree. The
  // shortcut is dock-scoped (the dock attaches its own keydown
  // listener), but it is documented here so the help drawer
  // surfaces the binding in the same table as every other
  // keyboard shortcut.
  {
    keys: ['Esc'],
    description: 'Clear scene selection from the layer-tools dock',
    group: 'global',
    shells: ['twin', 'workspace'],
    conditions: 'Tool card dock active',
    event: 'clear-scene-selection',
  },

  // Map navigation
  {
    keys: ['↑', '↓', '←', '→'],
    description: 'Pan map view',
    group: 'map',
    shells: ['twin', 'workspace'],
  },
  { keys: ['+', '='], description: 'Zoom in', group: 'map', shells: ['twin', 'workspace'], event: 'zoom-in' },
  { keys: ['-', '_'], description: 'Zoom out', group: 'map', shells: ['twin', 'workspace'], event: 'zoom-out' },
  { keys: ['F', 'f'], description: 'Fit to visible layers', group: 'map', shells: ['twin', 'workspace'], event: 'fit-to-visible' },
  { keys: ['L', 'l'], description: 'Focus layers panel', group: 'map', shells: ['twin', 'workspace'], event: 'focus-layers' },

  // Map tools
  { keys: ['M', 'm'], description: 'Activate measure tool', group: 'map', shells: ['twin', 'workspace'], event: 'tool-measure' },
  { keys: ['A', 'a'], description: 'Activate area tool', group: 'map', shells: ['twin', 'workspace'], event: 'tool-area' },
  { keys: ['P', 'p'], description: 'Activate pan tool', group: 'map', shells: ['twin', 'workspace'], event: 'tool-pan' },
  { keys: ['S', 's'], description: 'Activate select tool', group: 'map', shells: ['twin', 'workspace'], event: 'tool-select' },

  // 3D mode
  { keys: ['F', 'f'], description: 'Look at selected object', group: 'three-d', shells: ['twin', 'workspace'], conditions: '3D mode only', event: 'three-d-look-at-selected' },
  { keys: ['P', 'p'], description: 'Set pan camera mode', group: 'three-d', shells: ['twin', 'workspace'], conditions: '3D mode only', event: 'three-d-camera-pan' },
  { keys: ['R', 'r'], description: 'Reset to orbit camera and fit view', group: 'three-d', shells: ['twin', 'workspace'], conditions: '3D mode only', event: 'three-d-reset-orbit' },
  { keys: ['L', 'l'], description: 'Look at selected object', group: 'three-d', shells: ['twin', 'workspace'], conditions: '3D mode only', event: 'three-d-look-at-selected' },
  { keys: ['S', 's'], description: 'Toggle section plane', group: 'three-d', shells: ['twin', 'workspace'], conditions: '3D mode only', event: 'three-d-toggle-section' },
  { keys: ['I', 'i'], description: 'Toggle isolation mode', group: 'three-d', shells: ['twin', 'workspace'], conditions: '3D mode only', event: 'three-d-toggle-isolate' },
  { keys: ['G', 'g'], description: 'Toggle ghost mode', group: 'three-d', shells: ['twin', 'workspace'], conditions: '3D mode only', event: 'three-d-toggle-ghost' },

  // Point cloud
  { keys: ['S', 's'], description: 'Toggle slice offset', group: 'pointcloud', shells: ['twin', 'workspace'], conditions: 'Point cloud mode only', event: 'pointcloud-toggle-slice' },
  // WP-5 (point-cloud ToolCard): bracket keys adjust the live point size.
  // Bounded by the documented [0, 8] px range; 0 = "auto".
  { keys: ['['], description: 'Decrease point size', group: 'pointcloud', shells: ['twin', 'workspace'], conditions: 'Point cloud tool card active', event: 'pointcloud-decrease-point-size' },
  { keys: [']'], description: 'Increase point size', group: 'pointcloud', shells: ['twin', 'workspace'], conditions: 'Point cloud tool card active', event: 'pointcloud-increase-point-size' },
  { keys: ['1'], description: 'RGB color mode', group: 'pointcloud', shells: ['twin', 'workspace'], conditions: 'Point cloud mode only', event: 'pointcloud-color-rgb' },
  { keys: ['2'], description: 'Elevation color mode', group: 'pointcloud', shells: ['twin', 'workspace'], conditions: 'Point cloud mode only', event: 'pointcloud-color-elevation' },
  { keys: ['3'], description: 'Intensity color mode', group: 'pointcloud', shells: ['twin', 'workspace'], conditions: 'Point cloud mode only', event: 'pointcloud-color-intensity' },
  { keys: ['4'], description: 'Classification color mode', group: 'pointcloud', shells: ['twin', 'workspace'], conditions: 'Point cloud mode only', event: 'pointcloud-color-classification' },
  { keys: ['5'], description: 'Deviation color mode', group: 'pointcloud', shells: ['twin', 'workspace'], conditions: 'Point cloud mode only', event: 'pointcloud-color-deviation' },

  // Timeline (4D scrubber) - placeholder for future WP that implements timeline controls
  // TODO(WP-E13): Add 4D scrubber keyboard shortcuts when timeline controls are implemented

  // RFC-114 — LoR navigation
  { keys: ['H', 'h'], description: 'Return to globe view (LoR Home)', group: 'three-d', shells: ['twin'], conditions: '3D mode only', event: 'lor-home' },
];

/**
 * Get shortcuts grouped by their functional area for display purposes.
 * Returns a Map where keys are group names and values are arrays of shortcuts.
 */
export function getShortcutsByGroup(): Map<string, KeyboardShortcut[]> {
  const grouped = new Map<string, KeyboardShortcut[]>();

  for (const shortcut of KEYBOARD_SHORTCUTS) {
    const existing = grouped.get(shortcut.group) ?? [];
    existing.push(shortcut);
    grouped.set(shortcut.group, existing);
  }

  return grouped;
}

/**
 * Filter the shortcut list to a specific shell. Used by the help
 * drawer to show "Twin shortcuts" / "Workspace shortcuts" rows
 * separately while keeping a single source of truth.
 */
export function getShortcutsForShell(
  shell: KeyboardShortcutShell,
): KeyboardShortcut[] {
  return KEYBOARD_SHORTCUTS.filter((s) => s.shells.includes(shell));
}

/**
 * Format key combinations for display in UI.
 * Converts raw keys like ['Ctrl', 'S'] to platform-appropriate display.
 */
export function formatKeyCombo(keys: string[]): string {
  return keys.join(' ');
}

// ---------------------------------------------------------------------------
// Hook — unified keyboard shortcuts for both shells.
// ---------------------------------------------------------------------------

/**
 * Canonical CustomEvent name fired when a registered shortcut is
 * activated. Shells listen for this and route to their adapter
 * (TwinShell → engine + tool store; AppShell → existing handlers).
 */
export const VIEWER_SHORTCUT_EVENT = 'viewer:shortcut';

export interface ViewerShortcutDetail {
  readonly action: KeyboardShortcutAction;
  readonly key: string;
  readonly shell: KeyboardShortcutShell;
}

/**
 * Wires the shortcut keymap into the host shell. The TwinShell mounts
 * this with `shell="twin"` so the same M / A / P / S / F / L / R
 * shortcuts that AppShell honours are honoured on the Twin route too,
 * resolving the WP-2 verdict §10 gap that "TwinShell does not bind
 * S/P/A/M".
 *
 * Intentionally listens at the document level (capture phase) so the
 * shortcuts work whatever child element holds focus, but skips events
 * fired inside form controls (input / textarea / select /
 * contenteditable) so the user can still type in the global search /
 * filter inputs.
 *
 * Re-uses `KEYBOARD_SHORTCUTS` as the single dispatch table; the
 * help drawer is therefore guaranteed to stay in sync.
 */
export function useUnifiedKeyboardShortcuts(
  shell: KeyboardShortcutShell,
  options: {
    enabled?: boolean;
  } = {},
): void {
  const enabled = options.enabled ?? true;
  useEffect(() => {
    if (!enabled) return;
    if (typeof document === 'undefined') return;

    // Build a key → shortcut lookup, restricted to the host shell.
    const keymap = new Map<string, KeyboardShortcut>();
    for (const shortcut of KEYBOARD_SHORTCUTS) {
      if (!shortcut.shells.includes(shell)) continue;
      if (!shortcut.event) continue;
      for (const key of shortcut.keys) {
        if (!keymap.has(key)) keymap.set(key, shortcut);
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('input,textarea,select,[contenteditable="true"]')) {
        return;
      }
      const shortcut = keymap.get(event.key);
      if (!shortcut?.event) return;
      // Always claim the event so the browser does not also act on it
      // (e.g. "/" opens the browser's quick-find on some agents).
      event.preventDefault();
      const detail: ViewerShortcutDetail = {
        action: shortcut.event,
        key: event.key,
        shell,
      };
      document.dispatchEvent(
        new CustomEvent<ViewerShortcutDetail>(VIEWER_SHORTCUT_EVENT, {
          detail,
        }),
      );
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled, shell]);
}
